import { useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';

import { userService } from '#/client/api';
import { PreferencesResponse } from '#/client/models';
import { ignoreError } from '#/service/utils';
import { authActions, useAuthStore } from '#/store/auth/auth.store';
import { snackbarActions } from '#/store/snackbar/snackbar.store';

const USER_PREFERENCES_KEY = 'userPreferences';

export type UsePreferences = ReturnType<typeof usePreferences>;

/**
 *  Handles user profile preferences - only for multi-select input type
 *  This should be removed after multi-select is handled by form
 */
export const usePreferences = () => {
  const {
    isInitialised,
    profile: { preferences },
  } = useAuthStore();

  const [isPreferencesVisible, setIsPreferencesVisible] = useState(false);
  const [isPreferencesUpdating, setIsPreferencesUpdating] = useState(false);

  // Fetch all profile preferences and filter out the multi-select input. See "getMultiSelectInput" for details on why.
  const { isLoading, data: multiSelectInput = null } = useSWR(
    isInitialised ? USER_PREFERENCES_KEY : null,
    () =>
      userService
        .getPreferences()
        .then(getMultiSelectInput)
        .catch((err) => {
          console.debug('Getting user preferences failed', { err });
        }),
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      shouldRetryOnError: false,
    }
  );

  /**
   * Update a single multi-select preference at a time.
   * Performs optimistic UI update first, then updates backend
   */
  const handleUpdatePreferences = useCallback(
    (selectedValues: string[], showSnackbar = true) => {
      setIsPreferencesUpdating(true);

      const { key = '', values = [] } = multiSelectInput ?? {};

      // filter out old values from preferences
      const valueKeys = new Set(values.map(({ key }) => key));
      const filteredValues = selectedValues.filter((key) => valueKeys.has(key));

      // for optimistic UI approach setting preferences to auth store first
      authActions.setProfilePreferences(key, filteredValues);

      return userService
        .updatePreferences({ [key]: filteredValues })
        .then(() => {
          if (showSnackbar) {
            snackbarActions.showSnackbar({
              message: 'settings.account.profile.update.success.message',
            });
          }
        })
        .catch((err) => {
          console.debug('updateProfilePreferences failed', {
            key,
            filteredValues,
            err,
          });

          throw err;
        })
        .finally(() => {
          // refresh profile to make sure we are aligned with what is actually stored in core
          authActions.refreshProfile().catch(ignoreError);
          setIsPreferencesUpdating(false);
        });
    },
    [multiSelectInput]
  );

  const selectedPreferences = useMemo(
    () => preferences?.[multiSelectInput?.key ?? ''] ?? [],
    [preferences, multiSelectInput]
  );

  const handleOpenPreferences = useCallback(() => {
    setIsPreferencesVisible(true);
  }, []);

  const handleClosePreferences = useCallback(() => {
    setIsPreferencesVisible(false);
  }, []);

  return {
    isPreferencesLoading: !isInitialised || isLoading,
    isPreferencesUpdating,
    isPreferencesVisible,
    multiSelectInput,
    selectedPreferences,
    handleUpdatePreferences,
    handleOpenPreferences,
    handleClosePreferences,
  };
};

/**
 * Find the multi-select input from the preferences response.
 *
 * This is a temporary solution assuming there is only one multi-select input and we need to remove this logic in the future if we want to support the full display structure.
 */
export const getMultiSelectInput = ({ data }: { data: PreferencesResponse }) =>
  data.display?.find?.((display) => display?.variant === 'multi_select') ??
  null;
